import { useContext } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import PropTypes from 'prop-types';
import styles from './SbSharedRecentlySoldItems.scss';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { SharedItemTile } from '../../shared/SharedItemTile/SharedItemTile';
import { BarHeader } from 'dibs-elements/exports/BarHeader';
import { QuickViewProvider } from '../../global/QuickViewProvider/QuickViewProvider';
import {
    SbSharedThinBrowseItemTrackingProvider,
    SbSharedThinBrowseItemTrackingContext,
} from '../SbSharedThinBrowseItemTrackingProvider/SbSharedThinBrowseItemTrackingProvider';
import { ServerVarsContextConsumer } from '../../global/ServerVarsContext/ServerVarsContext';

const SbSharedRecentlySoldItemsComponent = ({
    itemSearch,
    user,
    currency,
    isClient,
    viewer,
    thinBrowseSoldItems,
}) => {
    const { fireThinBrowseItemsImpressionTracking, fireItemClickTracking } = useContext(
        SbSharedThinBrowseItemTrackingContext
    );

    return (
        <ServerVarsContextConsumer>
            {({ isMobile }) => (
                <>
                    <BarHeader
                        title={
                            <FormattedMessage
                                id="sb.SbSharedRecentlySoldItems.title"
                                defaultMessage="Previously Available Items"
                            />
                        }
                    />
                    <QuickViewProvider>
                        {({ quickView }) => {
                            return (
                                <div
                                    className={styles.container}
                                    data-tn="previously-available-items"
                                >
                                    {thinBrowseSoldItems.map(({ node }, index) => (
                                        <SharedItemTile
                                            isClient={isClient}
                                            key={node?.item?.serviceId}
                                            index={index}
                                            item={node?.item}
                                            user={null}
                                            currency={currency}
                                            isVerifiedTrade={user?.isVerifiedTrade}
                                            showSellerName={false}
                                            viewer={viewer}
                                            quickViewProps={quickView}
                                            itemSearch={itemSearch}
                                            isMobile={isMobile}
                                            onContentVisible={() =>
                                                fireThinBrowseItemsImpressionTracking(index)
                                            }
                                            onClick={() => fireItemClickTracking(index)}
                                        />
                                    ))}
                                </div>
                            );
                        }}
                    </QuickViewProvider>
                </>
            )}
        </ServerVarsContextConsumer>
    );
};

SbSharedRecentlySoldItemsComponent.propTypes = {
    itemSearch: PropTypes.object.isRequired,
    user: PropTypes.object,
    viewer: PropTypes.object,
    currency: PropTypes.string.isRequired,
    isClient: PropTypes.bool.isRequired,
    thinBrowseSoldItems: PropTypes.array.isRequired,
};

const SbSharedRecentlySoldItemsConsumer = ({ itemSearch, user, currency, isClient, viewer }) => {
    const thinBrowseSoldItems = itemSearch?.thinBrowseSoldResults?.edges || [];
    if (thinBrowseSoldItems.length === 0) {
        return null;
    }

    return (
        <SbSharedThinBrowseItemTrackingProvider
            itemSearch={itemSearch}
            viewer={viewer}
            items={itemSearch?.thinBrowseSoldResults?.edges.map(edge =>
                edge ? edge.node.item : null
            )}
            thinBrowseListingType="previously available"
        >
            <SbSharedRecentlySoldItemsComponent
                itemSearch={itemSearch}
                user={user}
                currency={currency}
                isClient={isClient}
                viewer={viewer}
                thinBrowseSoldItems={thinBrowseSoldItems}
            />
        </SbSharedThinBrowseItemTrackingProvider>
    );
};

SbSharedRecentlySoldItemsConsumer.propTypes = {
    itemSearch: PropTypes.object.isRequired,
    user: PropTypes.object,
    viewer: PropTypes.object,
    currency: PropTypes.string.isRequired,
    isClient: PropTypes.bool.isRequired,
};

export const SbSharedRecentlySoldItems = createFragmentContainer(
    SbSharedRecentlySoldItemsConsumer,
    {
        itemSearch: graphql`
            fragment SbSharedRecentlySoldItems_itemSearch on ItemSearchQueryConnection {
                ...SharedItemTile_itemSearch
                ...SbSharedThinBrowseItemTrackingProvider_itemSearch
                thinBrowseSoldResults {
                    edges {
                        node {
                            item {
                                ...SbSharedThinBrowseItemTrackingProvider_items
                                ...SharedItemTile_item
                                serviceId
                            }
                        }
                    }
                }
            }
        `,
        user: graphql`
            fragment SbSharedRecentlySoldItems_user on User {
                isVerifiedTrade
            }
        `,
        viewer: graphql`
            fragment SbSharedRecentlySoldItems_viewer on Viewer {
                ...SharedItemTile_viewer
                ...SbSharedThinBrowseItemTrackingProvider_viewer
            }
        `,
    }
);
